import * as React from 'react'

import { Stack, Skeleton } from '@mui/material'
import { styled } from '@mui/material/styles'

import { useCourseProgress, useCourse } from 'components/hooks/state'
import { I18n, useTranslation } from 'components/hooks/use-translation'
import ProgressBar from 'components/atomic/atoms/progress-bar'
import Text from 'components/atomic/atoms/text'
import Card from 'components/atomic/atoms/card'
import { Progress as ProgressType } from 'types/types'

const Info = styled('div')`
    display: flex;
    gap: 0.5rem;
`

const Progress = React.memo(({ courseId }: { courseId: string }) => {
    const [resourcesLeft, setResourcesLeft] = React.useState<string>()

    const { t }: I18n = useTranslation()
    const { data: progress } = useCourseProgress({ courseId })
    const { data: course } = useCourse({ courseId })

    const colors = React.useMemo(() => ['#D4E867', '#E86767', '#67E8D1', '#D667E8', '#679BE8'], [])
    const color = React.useCallback((index: number) => colors[index % colors.length], [colors])

    const calcProgressWidth = React.useCallback(
        (total: number) => (total * 100) / (progress?.modules?.length || 0),
        [progress?.modules.length],
    )

    const currentModule = React.useMemo(() => {
        if (progress && course) {
            return course.modules.find((m) => m._id === progress.current_module)
        }
    }, [progress, course])

    React.useEffect(() => {
        if (progress) {
            const stats = (progress.modules.find((m) => m._id === progress.current_module) as ProgressType)
                .resources_count

            const resources = Object.entries(stats)
                .reduce((acc, [key, res]) => {
                    const diff = res.total - res.completed
                    return diff === 1
                        ? [...acc, `${res.total - res.completed} ${t(`resources:types.${key.toUpperCase()}`)}`]
                        : diff > 1
                        ? [...acc, `${res.total - res.completed} ${t(`resources:types.plurals.${key.toUpperCase()}`)}`]
                        : acc
                }, [] as string[])
                .join(', ')

            setResourcesLeft(resources)
        }
    }, [progress, t])

    if (!progress) {
        return <ProgressLoading />
    }

    return (
        <Stack spacing="1rem">
            <Text variant="h4">{t('path:pathProgressSection.yourProgress')}</Text>
            <Card>
                <Stack spacing="0.5rem" direction="row">
                    {progress.modules.map((item, index) => (
                        <ProgressBar
                            background={color(index)}
                            backgroundFilled={color(index)}
                            sx={{ flex: calcProgressWidth(item.resources_total) / 10 }}
                            key={`path-macro${index}`}
                            type="outlined"
                            variant="determinate"
                            value={item.percent_complete}
                        />
                    ))}
                </Stack>

                <Stack spacing="0.5rem" mt="1rem">
                    <Text variant="h6" style={{ margin: '0.5rem 0' }}>
                        {t('path:progress.totalProgress', { value: progress.percent_complete.toFixed(2) })}
                    </Text>
                    <Info>
                        <Text>{t('path:progress.completedTasks')}</Text>
                        <Text color="primary">{`${progress.items_completed}/${progress.items_total}`}</Text>
                    </Info>
                    <Info>
                        <Text>{t('path:progress.currentModule')}</Text>
                        <Text color="primary">{currentModule?.title || '--'}</Text>
                    </Info>
                    <Info>
                        <Text>{t('path:progress.missingToComplete')}</Text>
                        <Text color="primary">{resourcesLeft}</Text>
                    </Info>
                    <Info>
                        <Text>{t('path:progress.timeToComplete')}</Text>
                        <Text color="primary">{`${Math.ceil(progress.time_to_complete / 60)}h`}</Text>
                    </Info>
                </Stack>
            </Card>
        </Stack>
    )
})

const ProgressLoading = () => {
    return (
        <Stack spacing="1rem">
            <Skeleton variant="rounded" width={'20%'} height="2rem" />
            <Skeleton variant="rounded" width={'100%'} height="12rem" />
        </Stack>
    )
}

export default Progress
